/**
 * The session panel over a Buddy draft: which session is open, the plan document it edits, the
 * dock's plumbing, and the saving of what is edited there.
 *
 * ── The panel is the builder's, and so is its document ───────────────────────────────────────
 * The panel is `features/plan-day-programming`'s day form in its own box, and that form edits the
 * plan document store (`entities/plan-draft`) through the store's named operations. So the draft
 * whose session is open is HOSTED in that store: read through the builder's reader
 * (`draftDocument`) and loaded as a document, with the draft's own slot tokens, so what the panel
 * edits is addressed exactly as the server addresses it. A draft has no id of its own (the wire
 * says 0), which the store treats as a stored plan: nothing about it is written to this device's
 * unsaved-plan record, and the route's release drops it.
 *
 * ── One draft at a time, and only Buddy's ────────────────────────────────────────────────────
 * The store holds one document. Opening a session of another draft version loads that version;
 * closing the panel leaves the document where it is. What is hosted is remembered with the
 * document it loaded, and the store still holds it only while its baseline IS that document: a
 * seed for the builder, or anything else loaded, ends it, and nothing of it is saved after that.
 *
 * ── Edits are saved to the draft, quietly ────────────────────────────────────────────────────
 * `hostedDraftSaver` sends the whole plan a moment after the last edit, against the draft revision
 * it was made on, and says what each save came to. While an edit is unsaved the local tree is the
 * truth: a draft the conversation re-reads replaces it only when its revision is NEWER than the
 * one the tree stands for and nothing here is unsaved.
 *
 * ── Nothing unsaved is let go without a save, or without asking ──────────────────────────────
 * The hosted document is let go when the trainer leaves the page, switches conversation or draft
 * version, starts a new conversation, or opens the draft in the builder. Each of those sends what
 * is unsaved first and waits for it; when it cannot be saved, the trainer is asked
 * (`leavePrompt`) whether to go without it. A page closed or reloaded sends it as a request that
 * outlives the page, and the browser's own prompt asks first.
 *
 * ── A proposal is applied to the hosted draft ────────────────────────────────────────────────
 * Buddy's proposal on a draft (ai-plan-builder R15 to R19) is applied through the same store
 * operation the builder uses, so its draft is hosted first (`proposalHost.hold`), whether or not a
 * session of it is open, and the saver above takes the apply to the server like any edit. Until it
 * is hosted, a proposal stands against the server's draft. A message about the draft carries the
 * newest draft as the trainer has it (`draftForMessage`, R13): the hosted tree when that draft is the
 * one hosted, with the revision the server holds.
 *
 * ── The address owns which session is open ───────────────────────────────────────────────────
 * `?sesja=<day's slot token>&wersja=<build turn>`. The version is always written, so a build that
 * lands while a session is open never moves the panel to the newer draft, and a reload of the
 * address reopens the same one. Opening is a history entry, so Back closes the panel; stepping to
 * the previous or next session and closing rewrite the address in place.
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate, useSearch, type ShouldBlockFn } from '@tanstack/react-router';
import { createElement, useEffect, useRef, useState } from 'react';

import {
  aiConversationQueryKey,
  editDraft,
  type AiDraftSnapshot,
  turnFromWire,
  withTurn,
  type AiBuildResult,
  type AiConversation,
} from '~/entities/ai-conversation';
import {
  draftToSnapshotRequest,
  loadPlanDraft,
  readPlanDraft,
  readPlanDraftBaseline,
  readPlanDraftRevision,
  releasePlanDraft,
  snapshotVersion,
  useDayDraft,
  usePlanDraftRevision,
  type PlanDraft,
} from '~/entities/plan-draft';
import type { DraftForMessage, ProposalHost } from '~/features/ai-chat';
import { useDayDock, type DayDockState } from '~/features/plan-day-programming';
import type { TrainWithMeModulesAiDtosResponsesAiConversationResponse as ConversationDto } from '~/shared/api/generated/model';

import { placeViolations, type PlacedViolation } from '../model/draftViolations';
import { ProposalExerciseChange } from '../ui/ProposalExerciseChange';

import { draftDocument } from './draftDocument';
import { createDraftSaver, type DraftSaveStatus, type DraftSaver } from './hostedDraftSaver';

/** What the last save came to, as the panel words it: a refusal's violations already placed. */
export type DraftSaveView =
  | Exclude<DraftSaveStatus, { readonly kind: 'invalid' }>
  | { readonly kind: 'invalid'; readonly violations: readonly PlacedViolation[] };

/** The trainer is asked whether to go on without edits that could not be saved. */
export interface LeavePrompt {
  readonly proceed: () => void;
  readonly stay: () => void;
}

export interface BuddySessionDockState {
  /** The session whose panel is open, and the draft it belongs to; `null` while it is closed. */
  readonly open: { readonly turnId: number; readonly daySlotToken: string } | null;
  readonly openSession: (turnId: number, daySlotToken: string) => void;
  /** The dock's plumbing, on the hosted document. */
  readonly dock: DayDockState;
  /** The session row owed focus: the one whose panel just closed, by Escape, the ✕ or Back. */
  readonly returnFocusToken: string | null;
  /** Names in the open draft still waiting for a decision: why „Otwórz w builderze" refuses. */
  readonly undecided: number;
  readonly save: DraftSaveView;
  /**
   * Send what the hosted draft has unsaved and wait for the answer, before something lets it go:
   * whichever version is hosted, whatever `turnId` names. `settled` is whether nothing unsaved is
   * left (a save that does not answer within `FLUSH_BOUND_MS` is not settled); `draft` is the draft
   * of build `turnId` as the server holds it afterwards.
   */
  readonly flush: (turnId?: number) => Promise<{ readonly settled: boolean; readonly draft: AiBuildResult | null }>;
  /**
   * A decision on build `turnId` is about to be written. Answers whether it may go (the hosted
   * edits of that same draft are saved; another draft's are not its concern) and, when it may,
   * the call that hands the panel's saving back once the decision answered, with the revision it
   * left (`null` when it was not taken).
   */
  readonly beforeDecision: (turnId: number) => Promise<((revision: number | null) => void) | null>;
  /** Ask before `proceed` loses edits that could not be saved; `proceed` runs on a yes. */
  readonly askToLeave: (proceed: () => void) => void;
  /** Let the hosted document go, before something else takes the store. Settle it first. */
  readonly letGo: () => void;
  readonly leavePrompt: LeavePrompt | null;
  /** Where a proposal on a Buddy draft stands and is applied: the hosted draft. */
  readonly proposalHost: ProposalHost;
  /** The newest draft as a message carries it (R13), or none before the first build. */
  readonly draftForMessage: () => DraftForMessage;
}

/** How long a save asked for before letting the draft go may take before the trainer is asked instead. */
export const FLUSH_BOUND_MS = 5_000;

/** The save's answer, or `false` once the bound passed: a stalled server never holds a navigation. */
function bounded(save: Promise<boolean>): Promise<boolean> {
  return Promise.race([
    save,
    new Promise<boolean>((resolve) => {
      setTimeout(() => {
        resolve(false);
      }, FLUSH_BOUND_MS);
    }),
  ]);
}

/** The finished drafts of a conversation, by their build turn. */
function builtDrafts(conversation: AiConversation | null): Map<number, AiBuildResult> {
  const drafts = new Map<number, AiBuildResult>();
  for (const turn of conversation?.turns ?? []) {
    if (turn.kind === 'build' && turn.status === 'done' && turn.result?.kind === 'build') drafts.set(turn.id, turn.result.result);
  }
  return drafts;
}

interface Hosted {
  readonly handle: string;
  readonly turnId: number;
  /** The document loaded into the store: the store holds it while its baseline is this object. */
  readonly document: PlanDraft;
  readonly saver: DraftSaver;
}

/** Whether a navigation lets the hosted draft go: off the page, to another conversation, or another version. */
function releasesTheDraft(
  { current, next }: Parameters<ShouldBlockFn>[0],
  hostedTurnId: number,
): boolean {
  if (current.routeId !== '/_shell/buddy' || next.routeId !== '/_shell/buddy') return true;
  return (
    next.search.rozmowa !== current.search.rozmowa ||
    next.search.klient !== current.search.klient ||
    (next.search.sesja !== undefined && next.search.wersja !== hostedTurnId)
  );
}

interface HostInput {
  readonly client: QueryClient;
  readonly handle: string;
  readonly turnId: number;
  readonly result: AiBuildResult;
  readonly document: PlanDraft;
  readonly conversationId: number;
  readonly onStatus: (saved: { readonly key: string; readonly status: DraftSaveView }) => void;
}

/** The hosted draft of build `turnId`, with the saver that sends its edits to that draft. */
function hostedDraft({ client, handle, turnId, result, document, conversationId, onStatus }: HostInput): Hosted {
  const key = `${handle}:${String(turnId)}`;
  const queryKey = aiConversationQueryKey(handle);
  return {
    handle,
    turnId,
    document,
    saver: createDraftSaver(
      { draftRevision: result.revision, savedAt: readPlanDraftRevision() },
      {
        read: () => {
          const draft = readPlanDraft();
          return draft !== null && readPlanDraftBaseline() === document ? { revision: readPlanDraftRevision(), draft } : null;
        },
        send: (plan, revision, keepalive) => editDraft(conversationId, turnId, plan, revision, keepalive),
        onStatus: (status) => {
          onStatus({
            key,
            status:
              status.kind === 'invalid'
                ? { kind: 'invalid', violations: placeViolations(status.violations, status.sent, readPlanDraft() ?? document) }
                : status,
          });
        },
        onSaved: (turn) => {
          client.setQueryData<ConversationDto>(queryKey, (current) => (current === undefined ? current : withTurn(current, turn)));
        },
        // The server's draft is read again, and the hook's effect takes it: this saver is finished.
        onStale: () => {
          void client.refetchQueries({ queryKey, exact: true });
        },
        onGone: () => {
          void client.invalidateQueries({ queryKey, exact: true });
        },
      },
    ),
  };
}

export function useBuddySessionDock(conversation: AiConversation | null, handle: string): BuddySessionDockState {
  const client = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_shell/buddy' });
  const drafts = builtDrafts(conversation);
  const conversationId = conversation?.id ?? null;
  // The draft the address names; an address from before the version was always written names
  // only the session, and means the newest.
  const turnId = search.sesja === undefined ? null : (search.wersja ?? [...drafts.keys()].at(-1) ?? null);
  const result = turnId === null ? null : (drafts.get(turnId) ?? null);
  const document = result === null ? null : draftDocument(result);

  // What the store holds. A ref, because it is bookkeeping about an external store; what the panel
  // renders from is the store itself.
  const hosted = useRef<Hosted | null>(null);
  // The last save's outcome, for the draft it was about: another draft open shows none.
  const [saved, setSaved] = useState<{ readonly key: string; readonly status: DraftSaveView } | null>(null);
  const hostKey = `${handle}:${String(turnId)}`;
  const storeRevision = usePlanDraftRevision();

  // The draft let go for something else (a seed for the builder) is not loaded back by the next
  // read of the conversation: only opening one of its sessions again hosts it.
  const letGoOf = useRef<string | null>(null);
  const letGo = () => {
    const held = hosted.current;
    hosted.current = null;
    if (held === null) return;
    letGoOf.current = `${held.handle}:${String(held.turnId)}`;
    held.saver.dispose();
    if (readPlanDraftBaseline() === held.document) releasePlanDraft();
  };

  // Load build `forTurnId`'s draft into the store, with a saver bound to it. What was hosted before
  // is let go first; the callers make sure nothing of it is unsaved.
  const host = (forTurnId: number, forResult: AiBuildResult, forDocument: PlanDraft, forConversationId: number) => {
    hosted.current?.saver.dispose();
    loadPlanDraft(forDocument);
    hosted.current = hostedDraft({
      client,
      handle,
      turnId: forTurnId,
      result: forResult,
      document: forDocument,
      conversationId: forConversationId,
      onStatus: setSaved,
    });
  };

  useEffect(() => {
    // A conversation switched away from takes its draft with it.
    if (hosted.current !== null && hosted.current.handle !== handle) letGo();
    if (turnId === null || result === null || document === null || conversationId === null) return;
    const held = hosted.current;
    if (held !== null && held.turnId === turnId && readPlanDraftBaseline() === held.document) {
      // Already hosted. A newer draft from the server replaces it only over nothing unsaved.
      if (result.revision <= held.saver.draftRevision() || held.saver.hasUnsaved()) return;
    }
    if (held === null && letGoOf.current === `${handle}:${String(turnId)}`) return;
    held?.saver.dispose();
    loadPlanDraft(document);
    hosted.current = hostedDraft({ client, handle, turnId, result, document, conversationId, onStatus: setSaved });
  }, [client, handle, turnId, result, document, conversationId]);

  /** Whether the store holds build `forTurnId`'s draft, as hosted here. */
  const holds = (forTurnId: number): boolean => {
    const held = hosted.current;
    return held !== null && held.turnId === forTurnId && readPlanDraftBaseline() === held.document;
  };

  const proposalHost: ProposalHost = {
    where: 'page',
    draftOf: (forTurnId, storeTree) => {
      if (forTurnId === null) return null;
      if (holds(forTurnId) && storeTree !== null) return storeTree;
      const forResult = drafts.get(forTurnId);
      return forResult === undefined ? null : draftDocument(forResult);
    },
    hold: (forTurnId) => {
      if (forTurnId === null) return false;
      if (holds(forTurnId)) return true;
      const forResult = drafts.get(forTurnId);
      const forDocument = forResult === undefined ? null : draftDocument(forResult);
      // Another version's edits not saved yet are never dropped for this one.
      if (forResult === undefined || forDocument === null || conversationId === null || hosted.current?.saver.hasUnsaved() === true) return false;
      letGoOf.current = null;
      host(forTurnId, forResult, forDocument, conversationId);
      return true;
    },
    renderExerciseChange: (change) => createElement(ProposalExerciseChange, { change }),
  };

  const draftForMessage = (): DraftForMessage => {
    const newest = [...drafts.entries()].at(-1);
    if (newest === undefined) return { kind: 'none' };
    const [newestTurnId, newestResult] = newest;
    const hostedTree = holds(newestTurnId) ? readPlanDraft() : null;
    const tree = hostedTree ?? draftDocument(newestResult);
    if (tree === null) return { kind: 'none' };
    const plan = draftToSnapshotRequest(tree);
    const snapshot: AiDraftSnapshot = {
      plan,
      version: snapshotVersion({ ...plan }),
      draftTurnId: newestTurnId,
      draftRevision: hostedTree !== null ? (hosted.current?.saver.draftRevision() ?? newestResult.revision) : newestResult.revision,
    };
    return { kind: 'ready', snapshot };
  };

  // Every change to the tree is the saver's to schedule; a load is one too, and it has nothing owed.
  useEffect(() => {
    hosted.current?.saver.edited();
  }, [storeRevision]);

  // The page going away: what is unsaved goes as a request that outlives it. Leaving the route lets
  // the draft go, after the same send.
  useEffect(() => {
    const onPageHide = () => {
      hosted.current?.saver.flushOnUnload();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      hosted.current?.saver.flushOnUnload();
      letGo();
    };
  }, []);

  const blocker = useBlocker({
    shouldBlockFn: async (navigation) => {
      const held = hosted.current;
      if (held === null || !held.saver.hasUnsaved() || !releasesTheDraft(navigation, held.turnId)) return false;
      return !(await bounded(held.saver.flush()));
    },
    enableBeforeUnload: () => hosted.current?.saver.hasUnsaved() ?? false,
    withResolver: true,
  });
  const [asked, setAsked] = useState<{ readonly proceed: () => void } | null>(null);
  let leavePrompt: LeavePrompt | null = null;
  if (blocker.status === 'blocked') {
    leavePrompt = {
      proceed: () => {
        letGo();
        blocker.proceed();
      },
      stay: blocker.reset,
    };
  } else if (asked !== null) {
    leavePrompt = {
      proceed: () => {
        setAsked(null);
        letGo();
        asked.proceed();
      },
      stay: () => {
        setAsked(null);
      },
    };
  }

  // The panel opens once the store HOLDS the session the address names: the day's own node, which
  // is there from the commit after the load above and is `null` for a token no draft carries.
  const hostedDay = useDayDraft(document === null ? null : (search.sesja ?? null));
  const daySlotToken = hostedDay === null ? null : hostedDay.slotToken;

  // The row whose panel closed last, however it closed: Back closes it without the dock knowing.
  const [lastOpen, setLastOpen] = useState<string | null>(null);
  if (daySlotToken !== null && daySlotToken !== lastOpen) setLastOpen(daySlotToken);

  const go = (next: { readonly sesja?: string; readonly wersja?: number }, push: boolean) => {
    void navigate({
      to: '/buddy',
      search: (current) => {
        const { sesja: _sesja, wersja: _wersja, ...rest } = current;
        return { ...rest, ...next };
      },
      replace: !push,
    });
  };

  const dock = useDayDock({
    daySlotToken,
    dock: daySlotToken === null ? null : 'panel',
    // One box on this page: the side panel.
    defaultDock: () => 'panel',
    open: (token) => {
      letGoOf.current = null;
      go({ sesja: token, ...(turnId === null ? {} : { wersja: turnId }) }, true);
    },
    select: (token) => {
      go({ sesja: token, ...(turnId === null ? {} : { wersja: turnId }) }, false);
    },
    close: () => {
      go({}, false);
    },
    switchHost: () => undefined,
  });

  return {
    open: turnId !== null && daySlotToken !== null ? { turnId, daySlotToken } : null,
    openSession: (forTurnId, token) => {
      letGoOf.current = null;
      go({ sesja: token, wersja: forTurnId }, search.sesja === undefined);
    },
    dock,
    returnFocusToken: daySlotToken === null ? lastOpen : null,
    undecided: result === null ? 0 : result.unresolved.filter((exercise) => exercise.decision === null).length,
    save: saved !== null && saved.key === hostKey ? saved.status : { kind: 'idle' },
    flush: async (forTurnId) => {
      const held = hosted.current;
      const settled = held === null || (await bounded(held.saver.flush()));
      if (forTurnId === undefined) return { settled, draft: null };
      const turn = (client.getQueryData<ConversationDto>(aiConversationQueryKey(handle))?.turns ?? []).find((each) => each.id === forTurnId);
      const read = turn === undefined ? null : turnFromWire(turn);
      return { settled, draft: read?.result?.kind === 'build' ? read.result.result : null };
    },
    beforeDecision: async (forTurnId) => {
      const held = hosted.current;
      if (held === null || held.turnId !== forTurnId) return () => undefined;
      if (!(await bounded(held.saver.flush()))) return null;
      held.saver.hold();
      return (revision) => {
        held.saver.resume(revision);
      };
    },
    askToLeave: (proceed) => {
      setAsked({ proceed });
    },
    letGo,
    leavePrompt,
    proposalHost,
    draftForMessage,
  };
}
